import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useState } from "react";
import { api } from "../../api";
import { DEPARTMENT_FIELDS, snakeToCamel } from "../../admin/fields";
import { RecordForm } from "../../admin/RecordForm";
import { ListEditor } from "../../admin/ListEditor";
import { SourcesPanel } from "../../admin/SourcesPanel";
import { VersionsPanel } from "../../admin/VersionsPanel";

type Dept = { id: string; name: string; acronym: string | null; level: string; state_code: string | null };
type Raw = { department: Record<string, unknown> & { id: string; name: string }; lists: { contacts: Record<string, unknown>[]; faqs: Record<string, unknown>[] } };

export function DepartmentsAdmin() {
  const { id } = useSearch({ from: "/app/admin/departments" });
  const navigate = useNavigate({ from: "/admin/departments" });
  const qc = useQueryClient();
  const [filter, setFilter] = useState("");
  const list = useQuery({ queryKey: ["departments"], queryFn: () => api<{ departments: Dept[] }>("/api/directory/departments") });
  const raw = useQuery({ queryKey: ["dept-raw", id], enabled: !!id && id !== "new", queryFn: () => api<Raw>(`/api/admin/directory/departments/${id}/raw`) });
  const reload = () => { qc.invalidateQueries({ queryKey: ["departments"] }); qc.invalidateQueries({ queryKey: ["department"] }); qc.invalidateQueries({ queryKey: ["dept-raw", id] }); qc.invalidateQueries({ queryKey: ["versions", "department", id] }); qc.invalidateQueries({ queryKey: ["sources", "department", id] }); };
  const parentOptions = (list.data?.departments ?? []).filter((d) => d.id !== id).map((d) => ({ value: d.id, label: d.name }));
  const shown = (list.data?.departments ?? []).filter((d) => `${d.name} ${d.acronym ?? ""} ${d.state_code ?? ""}`.toLowerCase().includes(filter.toLowerCase()));

  return (
    <div className="mx-auto grid max-w-6xl gap-4 lg:grid-cols-3">
      <section className="card flex flex-col gap-2">
        <div className="flex items-center justify-between"><h1 className="text-lg font-semibold">Departments</h1>
          <button className="btn-primary" onClick={() => navigate({ search: { id: "new" } })}>New</button></div>
        <input className="input" placeholder="Filter" aria-label="Filter departments" value={filter} onChange={(e) => setFilter(e.target.value)} />
        <ul className="max-h-[70vh] overflow-auto text-sm">{shown.map((d) => (
          <li key={d.id}><button className={`w-full rounded px-2 py-1 text-left hover:bg-slate-100 dark:hover:bg-slate-800 ${d.id === id ? "bg-brand-50 font-semibold" : ""}`}
            onClick={() => navigate({ search: { id: d.id } })}>{d.name}{d.acronym && ` (${d.acronym})`}<span className="ml-1 text-xs text-slate-500">{d.state_code ?? d.level}</span></button></li>
        ))}</ul>
      </section>
      <div className="flex flex-col gap-4 lg:col-span-2">
        {id === "new" && <section className="card"><h2 className="mb-3 font-semibold">New department</h2>
          <RecordForm fields={DEPARTMENT_FIELDS} sendAll initial={{ level: "federal" }} submitLabel="Create" selectOptions={{ parentId: parentOptions }}
            onSave={async (v) => { const r = await api<{ id: string }>("/api/admin/directory/departments", { method: "POST", body: v }); reload(); navigate({ search: { id: r.id } }); }} />
        </section>}
        {raw.data && <>
          <section className="card"><h2 className="mb-3 font-semibold">{raw.data.department.name}</h2>
            <RecordForm key={id} fields={DEPARTMENT_FIELDS} initial={snakeToCamel(raw.data.department)} submitLabel="Save" selectOptions={{ parentId: parentOptions }}
              onSave={async (v) => { await api(`/api/admin/directory/departments/${id}`, { method: "PATCH", body: v }); reload(); }} />
          </section>
          {(["contacts", "faqs"] as const).map((kind) => (
            <section key={kind} className="card"><h3 className="mb-3 text-sm font-semibold">{kind === "contacts" ? "Contacts" : "FAQs"}</h3>
              <ListEditor key={`${id}--${kind}`} kind={kind} items={raw.data!.lists[kind].map(snakeToCamel)}
                onSave={async (items) => { await api(`/api/admin/directory/lists/department/${id}/${kind}`, { method: "PUT", body: items }); reload(); }} />
            </section>
          ))}
          <section className="card"><SourcesPanel entityType="department" entityId={id!} /></section>
          <section className="card"><h3 className="mb-2 text-sm font-semibold">History</h3><VersionsPanel type="department" id={id!} /></section>
        </>}
        {(list.error || raw.error) && <p role="alert" className="text-sm text-red-600">{((list.error ?? raw.error) as Error).message}</p>}
        {!id && <p className="text-sm text-slate-500">Choose a department, or create a new one.</p>}
      </div>
    </div>
  );
}
